import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { wpaCommerceHeaders, wpaLocalizedName, isWpaCommerceCustomerOrderAllowed, buildWpaCustomerSuspendedBlock } from './wpaCommerceFetch';
import WpaLayout, { WPA_LAYOUT_HEADER_HEIGHT } from './WpaLayout';
import { useWpaCustomer } from './WpaCustomerContext';
import WpaAddToCartModal from './WpaAddToCartModal';
import { addProductToCart } from './wpaCartStore';
import {
  isCommerceOutOfStock,
  isCatalogStatusActive,
  isCatalogOutOfStockStatus,
  isWpaListHiddenStopOrderingStatus,
  isWpaProductOrderable,
  formatWpaCatalogStatusLabel,
  sortWpaProducts,
} from './wpaProductUtils';
import { sortWpaCollections } from './wpaCollectionSort';

const PRIMARY = '#4CAF50';
const HEART = '#e53935';
const PAGE_SIZE = 20;
/** 底部固定列（padding＋按鈕）約略高度，供列表留白 */
const LIST_FOOTER_RESERVE_PX = 88;

const styles = {
  /** 與頂欄一起滾動時，搜尋＋分類凍結在 green header 正下方 */
  stickyFilters: {
    position: 'sticky',
    top: WPA_LAYOUT_HEADER_HEIGHT,
    zIndex: 900,
    background: '#fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  },
  searchBar: {
    display: 'flex',
    gap: 8,
    padding: '12px 16px',
    background: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
  },
  searchInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
  },
  searchBtn: {
    padding: '10px 16px',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
    flexShrink: 0,
  },
  filterBar: {
    padding: '8px 16px',
    background: '#fff',
    borderBottom: '1px solid #f0f0f0',
  },
  filterSelect: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    background: '#fff',
    color: '#333',
  },
  list: {
    padding: '0 0 0',
    paddingBottom: `calc(${LIST_FOOTER_RESERVE_PX}px + env(safe-area-inset-bottom, 0px))`,
  },
  bottomBar: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    padding: '12px 16px',
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
    zIndex: 900,
    boxSizing: 'border-box',
  },
  bottomBtn: {
    width: '100%',
    padding: '14px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  productRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    gap: 12,
  },
  productImg: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: 'cover',
    background: '#f5f5f5',
    flexShrink: 0,
  },
  productImgPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 8,
    background: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ccc',
    fontSize: 20,
    flexShrink: 0,
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: 15,
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  productItemNo: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  productPrice: {
    fontSize: 16,
    fontWeight: 700,
    color: PRIMARY,
  },
  outOfStock: {
    fontSize: 12,
    color: '#e53935',
    fontWeight: 600,
  },
  catalogStatus: {
    fontSize: 12,
    color: '#e65100',
    fontWeight: 600,
  },
  addBtn: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: '#fff',
    color: HEART,
    border: '1px solid #ffcdd2',
    fontSize: 22,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: 0,
  },
  addBtnCart: {
    color: PRIMARY,
    border: '1px solid #c8e6c9',
  },
  addBtnDisabled: {
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    color: '#bdbdbd',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
  loading: {
    textAlign: 'center',
    padding: 24,
    color: '#999',
  },
  loadMore: {
    textAlign: 'center',
    padding: 16,
    color: '#999',
    fontSize: 14,
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.75)',
    color: '#fff',
    padding: '8px 20px',
    borderRadius: 20,
    fontSize: 14,
    zIndex: 2000,
    maxWidth: '90vw',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  errorOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  errorBox: {
    width: '100%',
    maxWidth: 360,
    background: '#fff',
    borderRadius: 12,
    padding: '24px 20px 16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#c62828',
    marginBottom: 10,
  },
  errorBody: {
    fontSize: 14,
    color: '#333',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginBottom: 18,
  },
  errorOk: {
    width: '100%',
    padding: '12px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

/** 與「購物車＋」區分：此為加入推薦價格表，使用紅心。 */
function RecommendHeartIcon({ disabled }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <path
        fill={disabled ? '#bdbdbd' : HEART}
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
      />
    </svg>
  );
}

export default function WpaProductListPage() {
  const { t, currentLanguage } = useLanguage();
  const { customerSessionReady, currentCustomer } = useWpaCustomer();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [collectionId, setCollectionId] = useState(searchParams.get('collectionId') || '');
  const [toast, setToast] = useState('');
  const [addingProductId, setAddingProductId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [errorDialog, setErrorDialog] = useState(null);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const fetchCollections = useCallback(async () => {
    const token = localStorage.getItem('wpa_token');
    try {
      const res = await fetch('/api/wpa-commerce/collections', {
        headers: wpaCommerceHeaders(token, currentLanguage),
      });
      if (res.ok) {
        const json = await res.json();
        const list = json.data || json.items || json;
        setCollections(sortWpaCollections(Array.isArray(list) ? list : []));
      }
    } catch {
      // ignore
    }
  }, [currentLanguage]);

  const fetchProducts = useCallback(async (pageNum, append = false) => {
    const token = localStorage.getItem('wpa_token');
    const params = new URLSearchParams();
    if (collectionId) params.set('collectionId', collectionId);
    if (searchText) params.set('search', searchText);
    params.set('page', pageNum);
    params.set('pageSize', PAGE_SIZE);
    try {
      const cust = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
      if (cust?.id) params.set('customerId', cust.id);
    } catch { /* ignore */ }

    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const res = await fetch(`/api/wpa-commerce/products?${params}`, {
        headers: wpaCommerceHeaders(token, currentLanguage),
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await res.json();
      const raw = json.data || json.items || json;
      const all = Array.isArray(raw) ? raw : [];
      // 有搜尋關鍵字時，union 推薦列表中符合的項目（hasPriceCard=true）一併顯示；
      // 無搜尋時維持原行為，只顯示尚未加入推薦列表的商品。
      const items = sortWpaProducts(
        all.filter((p) => {
          if (isWpaListHiddenStopOrderingStatus(p.status)) return false;
          if (searchText) return true;
          return !p.hasPriceCard;
        }),
      );
      // 用後端原始筆數（未經前端 hasPriceCard 過濾）判斷是否還有更多頁；
      // 若用 items.length，一旦前端過濾掉部分商品導致不足 PAGE_SIZE，
      // 就會提早停止載入，造成後續商品消失。
      const apiTotal = typeof json.total === 'number' ? json.total : null;
      setHasMore(
        apiTotal !== null
          ? pageNum * PAGE_SIZE < apiTotal
          : all.length >= PAGE_SIZE,
      );

      if (append) {
        setProducts((prev) => sortWpaProducts([...prev, ...items]));
      } else {
        setProducts(items);
      }
    } catch (err) {
      console.error('Failed to fetch products', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [collectionId, searchText, navigate, companyId, currentLanguage]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  useEffect(() => {
    if (!customerSessionReady) return;
    setPage(1);
    setHasMore(true);
    fetchProducts(1, false);
  }, [collectionId, searchText, fetchProducts, customerSessionReady, currentCustomer?.id]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchProducts(nextPage, true);
      }
    }, { threshold: 0.1 });

    observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loading, loadingMore, page, fetchProducts]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (collectionId) params.set('collectionId', collectionId);
    if (searchText) params.set('search', searchText);
    setSearchParams(params);
  };

  const handleCollectionChange = (val) => {
    setCollectionId(val);
    const params = new URLSearchParams();
    if (val) params.set('collectionId', val);
    if (searchText) params.set('search', searchText);
    setSearchParams(params);
  };

  const showToast = (msg, durationMs = 2800) => {
    setToast(msg);
    setTimeout(() => setToast(''), durationMs);
  };

  const blockIfCustomerSuspended = () => {
    if (!currentCustomer || isWpaCommerceCustomerOrderAllowed(currentCustomer)) return false;
    setErrorDialog(buildWpaCustomerSuspendedBlock(currentCustomer, t));
    return true;
  };

  /** hasPriceCard=true 的推薦列表項目：開啟加入購物車 modal（➕） */
  const openAddModal = (product) => {
    if (isCommerceOutOfStock(product.availability)) {
      showToast(t('wpa.common.cannotOrderOutOfStock'), 3800);
      return;
    }
    if (!isCatalogStatusActive(product.status)) {
      showToast(
        t('wpa.common.cannotOrderInactiveStatus', {
          status: formatWpaCatalogStatusLabel(product.status, t) || '-',
        }),
        3800,
      );
      return;
    }
    if (blockIfCustomerSuspended()) return;
    setModalProduct(product);
    setModalOpen(true);
  };

  const finishAdd = (qty, createNew) => {
    if (!modalProduct) return;
    if (blockIfCustomerSuspended()) return;
    addProductToCart(modalProduct, qty, createNew, currentLanguage);
    setModalOpen(false);
    setModalProduct(null);
    const pn = wpaLocalizedName(modalProduct);
    if (createNew) {
      showToast(t('wpa.home.toastNewCart', { name: pn, qty }));
    } else {
      showToast(t('wpa.home.toastAddedLatest', { name: pn, qty }));
    }
  };

  const addRecommendPriceCard = async (product) => {
    if (addingProductId) return;
    if (isCommerceOutOfStock(product.availability)) {
      showToast(t('wpa.common.cannotOrderOutOfStock'), 3800);
      return;
    }
    if (!isCatalogStatusActive(product.status)) {
      showToast(
        t('wpa.common.cannotOrderInactiveStatus', {
          status: formatWpaCatalogStatusLabel(product.status, t) || '-',
        }),
        3800,
      );
      return;
    }
    let customer;
    try {
      customer = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
    } catch {
      customer = null;
    }
    if (!customer?.id) {
      showToast(t('wpa.productList.recommendNeedCustomer'));
      return;
    }

    const token = localStorage.getItem('wpa_token');
    setAddingProductId(product.id);
    try {
      const res = await fetch('/api/wpa-commerce/order-app-recommend-price-card', {
        method: 'POST',
        headers: {
          ...wpaCommerceHeaders(token, currentLanguage),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: product.id,
          customerId: customer.id,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (!res.ok) {
        const msg = json.message || json.error || t('wpa.productList.recommendFailed');
        showToast(msg);
        return;
      }
      showToast(t('wpa.productList.recommendAdded', { name: wpaLocalizedName(product) }));
      // 點 ❤️ 成功後，就地將按鈕切換為 ➕（不移除 item）
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, hasPriceCard: true } : p)),
      );
    } catch {
      showToast(t('wpa.productList.recommendFailed'));
    } finally {
      setAddingProductId(null);
    }
  };

  const handleHeaderBack = () => {
    if (collectionId) {
      navigate(`/${companyId}/wpa/categories`);
      return;
    }
    navigate(`/${companyId}/wpa/home`);
  };

  const listAwaitingSession = !customerSessionReady;

  return (
    <WpaLayout
      title={t('wpa.titles.products')}
      leadingMode="back"
      onBack={handleHeaderBack}
    >
      <div style={styles.stickyFilters}>
        <div style={styles.searchBar}>
          <input
            style={styles.searchInput}
            placeholder={t('wpa.common.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button type="button" style={styles.searchBtn} onClick={handleSearch}>{t('wpa.common.search')}</button>
        </div>
        <div style={styles.filterBar}>
          <select
            style={styles.filterSelect}
            value={collectionId}
            onChange={(e) => handleCollectionChange(e.target.value)}
          >
            <option value="">{t('wpa.common.allCategories')}</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{wpaLocalizedName(c)}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.list}>
        {(loading || listAwaitingSession) && <div style={styles.loading}>{t('wpa.common.loading')}</div>}
        {!loading && !listAwaitingSession && products.length === 0 && (
          <div style={styles.loading}>{t('wpa.common.noSearchResults')}</div>
        )}
        {products.map((p) => {
          const outStock = isCommerceOutOfStock(p.availability);
          const inactiveCatalog = !isCatalogStatusActive(p.status);
          const orderBlocked = !isWpaProductOrderable(p);
          const catalogOos = inactiveCatalog && isCatalogOutOfStockStatus(p.status);
          const stockLabel = outStock && !catalogOos ? t('wpa.common.outOfStock') : null;
          const statusLabel = inactiveCatalog ? (formatWpaCatalogStatusLabel(p.status, t) || '-') : null;
          const pName = wpaLocalizedName(p);
          const busy = addingProductId === p.id;
          const isRecommended = !!p.hasPriceCard;
          return (
            <div key={p.id} style={styles.productRow}>
              {p.imageUrl ? (
                <img src={p.imageUrl} alt={pName} style={styles.productImg} />
              ) : (
                <div style={styles.productImgPlaceholder}>📦</div>
              )}
              <div style={styles.productInfo}>
                <div style={styles.productName}>{pName}</div>
                <div style={styles.productItemNo}>{p.itemNo}</div>
                <div style={styles.priceRow}>
                  {p.showPrice !== false && p.price != null ? (
                    <div style={styles.productPrice} translate="no">${Number(p.price).toFixed(2)}</div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#999' }}>{t('wpa.common.priceInquire')}</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    {stockLabel && <div style={styles.outOfStock}>{stockLabel}</div>}
                    {statusLabel && <div style={styles.catalogStatus}>{statusLabel}</div>}
                  </div>
                </div>
              </div>
              {isRecommended ? (
                // 推薦列表中的商品（hasPriceCard=true）→ ➕ 加入購物車
                <button
                  type="button"
                  style={{
                    ...styles.addBtn,
                    ...styles.addBtnCart,
                    ...(orderBlocked ? styles.addBtnDisabled : {}),
                  }}
                  onClick={() => openAddModal(p)}
                  aria-disabled={orderBlocked}
                  aria-label={t('wpa.common.addToCartAria')}
                >
                  <span style={{ fontSize: 22, lineHeight: 1, color: orderBlocked ? '#bdbdbd' : PRIMARY }}>+</span>
                </button>
              ) : (
                // 未加入推薦列表的商品（hasPriceCard=false）→ ❤️ 加入推薦價格表
                <button
                  type="button"
                  style={{
                    ...styles.addBtn,
                    ...(orderBlocked || busy ? styles.addBtnDisabled : {}),
                  }}
                  onClick={() => addRecommendPriceCard(p)}
                  aria-disabled={orderBlocked || busy}
                  aria-label={t('wpa.productList.addRecommendAria')}
                >
                  {busy ? (
                    <span style={{ fontSize: 16, color: '#999' }}>…</span>
                  ) : (
                    <RecommendHeartIcon disabled={orderBlocked} />
                  )}
                </button>
              )}
            </div>
          );
        })}
        {loadingMore && <div style={styles.loadMore}>{t('wpa.common.loadMore')}</div>}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>

      <div style={styles.bottomBar}>
        <button
          type="button"
          style={styles.bottomBtn}
          onClick={() => navigate(`/${companyId}/wpa/home`)}
        >
          {t('wpa.productList.backToRecommended')}
        </button>
      </div>

      <WpaAddToCartModal
        open={modalOpen}
        product={modalProduct}
        onClose={() => { setModalOpen(false); setModalProduct(null); }}
        onAddToCurrent={(qty) => finishAdd(qty, false)}
        onAddToNew={(qty) => finishAdd(qty, true)}
      />

      {toast && <div style={styles.toast}>{toast}</div>}

      {errorDialog && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{t('wpa.common.cannotAddToCartTitle')}</div>
            <div style={styles.errorBody}>{errorDialog}</div>
            <button type="button" style={styles.errorOk} onClick={() => setErrorDialog(null)}>
              {t('wpa.cartDetail.errorOk')}
            </button>
          </div>
        </div>
      )}
    </WpaLayout>
  );
}
